import React, { useState, useEffect } from 'react';
import PinJobButton from './boards/PinJobButton';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ProjectLabourTab } from './ProjectLabourTab';
import JobMail from './mail/JobMail';
import { 
  ArrowLeft, 
  Calendar, 
  DollarSign, 
  Users, 
  Truck, 
  Package, 
  Settings, 
  FileText, 
  AlertCircle,
  CheckCircle,
  Clock,
  MapPin,
  Phone,
  Mail,
  Edit,
  Plus,
  ShoppingCart,
  Building2,
  User,
  Calendar as CalendarIcon
} from 'lucide-react';

const poDateKey = (d) => {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(d || '');
  return m ? `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}` : (d || '');
};

const ProjectDetail = () => {
  const { projectNumber } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [assignedWorkers, setAssignedWorkers] = useState([]);
  
  // Calculate total from all invoices
  const totalInvoices = invoices.reduce((sum, invoice) => {
    return sum + (invoice.total_amount || invoice.invoice_amount || 0);
  }, 0);
  const [equipmentRentals, setEquipmentRentals] = useState([]);
  const [ownedEquipment, setOwnedEquipment] = useState([]);
  const [laborData, setLaborData] = useState(null);
  const [dailyLaborData, setDailyLaborData] = useState(null);
  const [shopTrackerData, setShopTrackerData] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showDailyBreakdown, setShowDailyBreakdown] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [managers, setManagers] = useState([]);
  const [editingPM, setEditingPM] = useState(false);
  const [poLoading, setPOLoading] = useState(false);
  const [showInvoiceGen, setShowInvoiceGen] = useState(false);
  const [invoiceMarkup, setInvoiceMarkup] = useState({ labour: 0, consumables: 0, materials: 0 });
  const [invoiceConsSearch, setInvoiceConsSearch] = useState('');
  const [invoiceConsCatalog, setInvoiceConsCatalog] = useState([]);
  const [invoiceConsItems, setInvoiceConsItems] = useState({});
  const [reconcileData, setReconcileData] = useState(null); // {workOrderPrice, currentTotal, baseCost}
  const [reconcileBusy, setReconcileBusy] = useState(false);
  const [editingConsId, setEditingConsId] = useState(null);
  const [consEdit, setConsEdit] = useState({ item_name: '', quantity: 0, unit_cost: 0 });
  const [consSaving, setConsSaving] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const authToken = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${authToken}` };
  const token = localStorage.getItem('token');

  // Handle edit project navigation
  const handleEditProject = () => {
    // Navigate back to projects page with edit mode for this project
    navigate(`/projects?edit=${project.project_number}`, { 
      state: { editProject: project } 
    });
  };

  // Handle employee click to show daily breakdown
  const handleEmployeeClick = (employeeName) => {
    if (dailyLaborData && dailyLaborData.employee_actual_data) {
      const employeeData = dailyLaborData.employee_actual_data.find(
        emp => emp.employee_name.toLowerCase() === employeeName.toLowerCase()
      );
      if (employeeData) {
        setSelectedEmployee(employeeData);
        setShowDailyBreakdown(true);
      }
    }
  };

  useEffect(() => {
    fetchProjectData();
    fetchManagers();
    // Standard fab markups (Admin / Shop Tracker → Fabrication T&M Sheets → "Save as standard") prefill the T&M invoice
    axios.get(`${backendUrl}/api/shop/fab-markups`, { headers })
      .then(r => setInvoiceMarkup({ labour: r.data.labour ?? 0, consumables: r.data.consumables ?? 0, materials: r.data.materials ?? 0 }))
      .catch(() => {});
  }, [projectNumber]);

  const fetchProjectData = async () => {
    try {
      setLoading(true);
      
      // Fetch project details - try specific project endpoint first
      let currentProject = null;
      
      // Try fetching single project by project number
      try {
        const singleProjectResponse = await fetch(`${backendUrl}/api/projects/${projectNumber}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (singleProjectResponse.ok) {
          currentProject = await singleProjectResponse.json();
        }
      } catch (err) {
        console.log('Single project fetch failed, trying all projects');
      }
      
      // Fallback: Fetch all projects and find the one we need
      if (!currentProject) {
        const projectResponse = await fetch(`${backendUrl}/api/projects`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (projectResponse.ok) {
          const projects = await projectResponse.json();
          currentProject = projects.find(p => p.project_number === projectNumber);
        }
      }
      
      setProject(currentProject);

      // Fetch project invoices
      const invoicesResponse = await fetch(`${backendUrl}/api/project-invoices/${projectNumber}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (invoicesResponse.ok) {
        const invoicesData = await invoicesResponse.json();
        // Handle the new response format with nested invoices
        if (invoicesData.invoices) {
          setInvoices(invoicesData.invoices);
        } else {
          // Fallback for direct array response
          setInvoices(Array.isArray(invoicesData) ? invoicesData : []);
        }
      }

      // Fetch purchase orders
      const poResponse = await fetch(`${backendUrl}/api/projects/${projectNumber}/purchase-orders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (poResponse.ok) {
        const poData = await poResponse.json();
        setPurchaseOrders((poData.purchase_orders || []).sort((a, b) => poDateKey(b.date).localeCompare(poDateKey(a.date))));
      }

      // Fetch assigned workers (from schedule assignments)
      const workersResponse = await fetch(`${backendUrl}/api/schedule/assignments?project=${projectNumber}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (workersResponse.ok) {
        const workersData = await workersResponse.json();
        setAssignedWorkers(workersData);
      }

      // Fetch QuickBooks labor data for this project
      try {
        const laborResponse = await fetch(`${backendUrl}/api/projects/${projectNumber}/labor-details`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (laborResponse.ok) {
          const laborData = await laborResponse.json();
          setLaborData(laborData);
        }
      } catch (error) {
        // Labor data is optional, don't fail if not available
        console.log('QuickBooks labor data not available for this project');
      }

      // Fetch QuickBooks actual labor data for this project (MOST ACCURATE)
      try {
        const actualLaborResponse = await fetch(`${backendUrl}/api/projects/${projectNumber}/actual-labor`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (actualLaborResponse.ok) {
          const actualLaborData = await actualLaborResponse.json();
          setDailyLaborData(actualLaborData);
          console.log('✅ Loaded actual labor data:', actualLaborData);
        }
      } catch (error) {
        // Actual labor data is optional, don't fail if not available
        console.log('QuickBooks actual labor data not available for this project');
      }

      // Fetch Shop Tracker labor + consumables for this project
      try {
        const shopResponse = await fetch(`${backendUrl}/api/shop/project-labor/${projectNumber}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        if (shopResponse.ok) {
          const shopData = await shopResponse.json();
          setShopTrackerData(shopData);
        } else {
          setShopTrackerData({ error: `Could not load hours (${shopResponse.status})`, employees: [], consumable_entries: [] });
        }
      } catch (error) {
        console.log('Shop tracker data not available for this project');
        setShopTrackerData({ error: 'Could not load hours', employees: [], consumable_entries: [] });
      }

      // TODO: Fetch equipment rentals and owned equipment for this project
      // These would require new API endpoints

    } catch (error) {
      console.error('Error fetching project data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-700';
      case 'in_progress': return 'text-amber-700';
      case 'planning': return 'text-blue-700';
      default: return 'text-slate-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return CheckCircle;
      case 'in_progress': return Clock;
      case 'planning': return AlertCircle;
      default: return AlertCircle;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // ── T&M Invoice generation + work-order price reconciliation ──
  const buildTMInvoiceHtml = (markup) => {
    const m = markup || invoiceMarkup;
    const labourCost = shopTrackerData?.total_labour_cost || 0;
    const consCost = shopTrackerData?.total_consumable_cost || 0;
    const poCost = purchaseOrders.reduce((s, po) => s + (po.total_amount || po.amount || 0), 0);
    const labourBill = labourCost * (1 + (m.labour || 0) / 100);
    const consBill = consCost * (1 + (m.consumables || 0) / 100);
    const matBill = poCost * (1 + (m.materials || 0) / 100);
    const totalBill = labourBill + consBill + matBill;
    const employees = shopTrackerData?.employees || [];
    const consumables = shopTrackerData?.consumable_entries || [];

    const labourRows = employees.map(e =>
      `<tr><td style="padding:6px 10px;border:1px solid #ddd">${e.employee_name}</td><td style="padding:6px 10px;border:1px solid #ddd;text-align:center">${e.total_hours?.toFixed(1)}h</td><td style="padding:6px 10px;border:1px solid #ddd;text-align:right">${formatCurrency(e.total_hours * (labourCost > 0 && shopTrackerData?.total_hours > 0 ? labourBill / shopTrackerData.total_hours : 85))}</td></tr>`
    ).join('');

    let consRows = '';
    const consByItem = {};
    consumables.forEach(c => {
      const k = c.item_name;
      if (!consByItem[k]) consByItem[k] = { qty: 0, cost: 0 };
      consByItem[k].qty += c.quantity || 0;
      consByItem[k].cost += c.total_cost || 0;
    });
    Object.entries(consByItem).forEach(([name, d]) => {
      const marked = d.cost * (1 + (m.consumables || 0) / 100);
      consRows += `<tr><td style="padding:6px 10px;border:1px solid #ddd">${name}</td><td style="padding:6px 10px;border:1px solid #ddd;text-align:center">${d.qty}</td><td style="padding:6px 10px;border:1px solid #ddd;text-align:right">${formatCurrency(marked)}</td></tr>`;
    });

    const poRows = purchaseOrders.map(po =>
      `<tr><td style="padding:6px 10px;border:1px solid #ddd">PO#${po.po_number}</td><td style="padding:6px 10px;border:1px solid #ddd">${po.supplier || po.vendor || ''}</td><td style="padding:6px 10px;border:1px solid #ddd;text-align:right">${formatCurrency((po.total_amount || po.amount || 0) * (1 + (m.materials || 0) / 100))}</td></tr>`
    ).join('');

    return `<html><head><title>T&M Invoice — ${project?.project_number}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:30px;color:#1e293b;max-width:800px;margin:0 auto}
        h1{font-size:22px;margin:0 0 4px}
        h2{font-size:14px;color:#475569;margin:0 0 24px}
        h3{font-size:13px;color:#1e3a5f;margin:24px 0 8px;text-transform:uppercase;letter-spacing:1px;border-bottom:2px solid #1e3a5f;padding-bottom:4px}
        table{border-collapse:collapse;width:100%;font-size:12px;margin-bottom:8px}
        th{background:#f1f5f9;padding:8px 10px;border:1px solid #cbd5e1;text-align:left;font-size:11px}
        td{padding:6px 10px;border:1px solid #ddd}
        .total{font-size:18px;font-weight:bold;text-align:right;padding:16px 0;border-top:3px solid #1e3a5f}
        .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px}
        .meta{text-align:right;font-size:12px;color:#64748b}
        @media print{@page{margin:1cm}}
      </style></head><body>
      <div class="header">
        <div>
          <h1>365 Industrial Solutions</h1>
          <h2>Time & Materials Invoice</h2>
        </div>
        <div class="meta">
          <p><strong>Project:</strong> ${project?.project_number}</p>
          <p><strong>Customer:</strong> ${project?.client_name}</p>
          <p><strong>Date:</strong> ${new Date().toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric'})}</p>
        </div>
      </div>

      <h3>Labour${(m.labour || 0) > 0 ? ' (+' + m.labour + '% markup)' : ''}</h3>
      <table><tr><th>Employee</th><th style="text-align:center">Hours</th><th style="text-align:right">Amount</th></tr>${labourRows}
      <tr style="background:#f0fdf4;font-weight:bold"><td colspan="2" style="text-align:right;padding:8px 10px;border:1px solid #ddd">Labour Subtotal</td><td style="text-align:right;padding:8px 10px;border:1px solid #ddd">${formatCurrency(labourBill)}</td></tr></table>

      ${consRows ? `<h3>Consumables${(m.consumables || 0) > 0 ? ' (+' + m.consumables + '% markup)' : ''}</h3>
      <table><tr><th>Item</th><th style="text-align:center">Qty</th><th style="text-align:right">Amount</th></tr>${consRows}
      <tr style="background:#f0fdf4;font-weight:bold"><td colspan="2" style="text-align:right;padding:8px 10px;border:1px solid #ddd">Consumables Subtotal</td><td style="text-align:right;padding:8px 10px;border:1px solid #ddd">${formatCurrency(consBill)}</td></tr></table>` : ''}

      ${poRows ? `<h3>Materials & Purchase Orders${(m.materials || 0) > 0 ? ' (+' + m.materials + '% markup)' : ''}</h3>
      <table><tr><th>PO#</th><th>Supplier</th><th style="text-align:right">Amount</th></tr>${poRows}
      <tr style="background:#f0fdf4;font-weight:bold"><td colspan="2" style="text-align:right;padding:8px 10px;border:1px solid #ddd">Materials Subtotal</td><td style="text-align:right;padding:8px 10px;border:1px solid #ddd">${formatCurrency(matBill)}</td></tr></table>` : ''}

      <div class="total">Total Due: ${formatCurrency(totalBill)}</div>

      <p style="font-size:10px;color:#94a3b8;margin-top:24px;text-align:center">Generated by 365Ops — ${new Date().toLocaleString()}</p>
    </body></html>`;
  };

  const printTMInvoice = (markup) => {
    const pw = window.open('', '_blank');
    if (!pw) { toast.error('Pop-up blocked — allow pop-ups to print the invoice'); return; }
    pw.document.write(buildTMInvoiceHtml(markup));
    pw.document.close();
    pw.print();
  };

  const computeTMTotals = () => {
    const labourCost = shopTrackerData?.total_labour_cost || 0;
    const consCost = shopTrackerData?.total_consumable_cost || 0;
    const poCost = purchaseOrders.reduce((s, po) => s + (po.total_amount || po.amount || 0), 0);
    const baseCost = labourCost + consCost + poCost;
    const currentTotal =
      labourCost * (1 + (invoiceMarkup.labour || 0) / 100) +
      consCost * (1 + (invoiceMarkup.consumables || 0) / 100) +
      poCost * (1 + (invoiceMarkup.materials || 0) / 100);
    return { baseCost, currentTotal };
  };

  // Click handler for the "Print Invoice" button — show reconcile popup only when
  // the work-order (preset) price differs from the 365Ops calculated total.
  const handleGenerateInvoice = () => {
    const { baseCost, currentTotal } = computeTMTotals();
    const workOrderPrice = totalInvoices;
    if (workOrderPrice > 0 && Math.abs(workOrderPrice - currentTotal) > 0.01) {
      setReconcileData({ workOrderPrice, currentTotal, baseCost });
    } else {
      printTMInvoice(invoiceMarkup);
    }
  };

  // Option A — scale line items up to the work-order price via auto-markup
  const matchWorkOrderPrice = () => {
    const { workOrderPrice, baseCost } = reconcileData;
    if (baseCost <= 0) {
      toast.error('No labour/material costs to scale to the work-order price');
      return;
    }
    const pct = Math.round(((workOrderPrice / baseCost) - 1) * 1000) / 10; // 1 decimal
    const newMarkup = { labour: pct, consumables: pct, materials: pct };
    setInvoiceMarkup(newMarkup);
    setReconcileData(null);
    printTMInvoice(newMarkup);
  };

  // Option B — overwrite the work-order price with the 365Ops calculated total
  const overwriteWorkOrderPrice = async () => {
    const { currentTotal } = reconcileData;
    setReconcileBusy(true);
    try {
      await axios.post(
        `${backendUrl}/api/project-invoices/${project.project_number}/set-total`,
        { total: currentTotal },
        { headers }
      );
      toast.success(`Work-order price updated to ${formatCurrency(currentTotal)}`);
      setReconcileData(null);
      printTMInvoice(invoiceMarkup);
      fetchProjectData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update the work-order price');
    } finally {
      setReconcileBusy(false);
    }
  };

  // ── Edit a consumable entry — saves to the source record so it updates the
  //    project total, the T&M invoice and the Shop Tracker everywhere ──
  const startConsEdit = (c) => {
    const unit = c.unit_cost != null ? c.unit_cost : (c.quantity ? (c.total_cost || 0) / c.quantity : 0);
    setEditingConsId(c.id);
    setConsEdit({ item_name: c.item_name || '', quantity: c.quantity || 0, unit_cost: Math.round(unit * 100) / 100 });
  };

  const cancelConsEdit = () => {
    setEditingConsId(null);
    setConsEdit({ item_name: '', quantity: 0, unit_cost: 0 });
  };

  const saveConsEdit = async (entryId) => {
    if (!consEdit.item_name.trim()) { toast.error('Item name is required'); return; }
    setConsSaving(true);
    try {
      await axios.put(
        `${backendUrl}/api/shop/consumable-entries/${entryId}`,
        {
          item_name: consEdit.item_name.trim(),
          quantity: Number(consEdit.quantity),
          unit_cost: Number(consEdit.unit_cost),
        },
        { headers }
      );
      toast.success('Consumable updated everywhere');
      cancelConsEdit();
      fetchProjectData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update consumable');
    } finally {
      setConsSaving(false);
    }
  };


  // Fetch managers for PM selection
  const fetchManagers = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        const users = data.users || data || [];
        const pmUsers = users.filter(u => 
          u.role === 'project_manager_estimator' || 
          u.role === 'super_admin' || 
          u.role === 'owner'
        );
        setManagers(pmUsers);
      }
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };

  // Update PM
  const updatePM = async (newPM) => {
    console.log('🔄 Updating PM to:', newPM, 'Project ID:', project.id);
    
    if (!newPM || newPM === project.assigned_manager) {
      console.log('⏭️ Skipping update - same PM or empty');
      setEditingPM(false);
      return;
    }
    
    try {
      const response = await fetch(`${backendUrl}/api/projects/${project.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ assigned_manager: newPM })
      });
      
      if (response.ok) {
        console.log('✅ PM updated successfully');
        setProject(prev => ({ ...prev, assigned_manager: newPM }));
        setEditingPM(false);
      } else {
        const error = await response.json();
        console.error('❌ Failed to update PM:', error);
        alert('Failed to update PM: ' + (error.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Error updating PM:', error);
      alert('Error updating PM: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen ops-canvas text-slate-900 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen ops-canvas text-slate-900 p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Project Not Found</h1>
          <p className="text-slate-500 mb-6">Project {projectNumber} could not be found.</p>
          <button
            onClick={() => navigate('/projects')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  const StatusIcon = getStatusIcon(project.status);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50">
      {/* Header */}
      <div className="relative z-30 bg-white/60 backdrop-blur-xl border-b border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/projects')}
              className="p-2 hover:bg-white/60 rounded-lg transition-colors text-slate-800"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{project.title}</h1>
              <p className="text-slate-600 mt-1">Project #{project.project_number}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className={`flex items-center space-x-2 ${getStatusColor(project.status)}`}>
              <StatusIcon className="h-5 w-5" />
              <span className="capitalize">{project.status.replace('_', ' ')}</span>
            </div>
            <PinJobButton wo={project.project_number} />
            <button 
              onClick={handleEditProject}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
            >
              <Edit className="h-4 w-4" />
              <span>Edit Project</span>
            </button>
            <button 
              onClick={() => setShowInvoiceGen(true)}
              data-testid="generate-tm-invoice-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
            >
              <FileText className="h-4 w-4" />
              <span>Generate T&M Invoice</span>
            </button>
          </div>
        </div>
      </div>

      {/* Project Overview Cards */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-50/70 to-cyan-50/70 backdrop-blur-xl p-6 rounded-lg border border-blue-200/50 shadow-lg">
            <div className="flex items-center space-x-3">
              <DollarSign className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-sm text-slate-600">Total Value</p>
                <p className="text-xl font-bold text-green-700">{formatCurrency(project.estimated_budget)}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/60 backdrop-blur-xl p-6 rounded-lg border border-slate-200">
            <div className="flex items-center space-x-3">
              <FileText className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-sm text-slate-600">Invoices</p>
                <p className="text-xl font-bold text-blue-700">{invoices.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/60 backdrop-blur-xl p-6 rounded-lg border border-slate-200">
            <div className="flex items-center space-x-3">
              <ShoppingCart className="h-8 w-8 text-orange-600" />
              <div>
                <p className="text-sm text-slate-600">Purchase Orders</p>
                <p className="text-xl font-bold text-orange-700">{purchaseOrders.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/60 backdrop-blur-xl p-6 rounded-lg border border-slate-200">
            <div className="flex items-center space-x-3">
              <Users className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-sm text-slate-600">
                  {laborData ? 'Labor Hours' : 'Assigned Workers'}
                </p>
                <p className="text-xl font-bold text-purple-700">
                  {laborData ? `${laborData.total_hours}h` : assignedWorkers.length}
                </p>
                {laborData && (
                  <p className="text-xs text-slate-500">{laborData.employee_count} workers</p>
                )}
              </div>
            </div>
          </div>
          
          <div className="bg-white/60 backdrop-blur-xl p-6 rounded-lg border border-slate-200">
            <div className="flex items-center space-x-3">
              <Calendar className="h-8 w-8 text-yellow-600" />
              <div>
                <p className="text-sm text-slate-600">Start Date</p>
                <p className="text-xl font-bold text-yellow-700">{formatDate(project.start_date)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-slate-200">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'overview', label: 'Overview', icon: FileText },
                { id: 'billing', label: 'Invoices & Billing', icon: DollarSign },
                { id: 'purchase-orders', label: 'Purchase Orders', icon: ShoppingCart },
                { id: 'team', label: 'Labour', icon: Users },
                { id: 'equipment', label: 'Equipment', icon: Truck },
                { id: 'materials', label: 'Consumables', icon: Package },
                { id: 'mail', label: 'Mail', icon: Mail }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`project-tab-${tab.id}`}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-400'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'mail' && <JobMail reference={project.project_number} />}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Project Details */}
                <div className="bg-gradient-to-br from-white/80 to-blue-50/50 backdrop-blur-xl p-6 rounded-xl border border-blue-200/40 shadow-lg">
                  <h3 className="text-lg font-semibold mb-4 text-slate-900">Project Details</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-slate-600 font-medium">Client</p>
                      <p className="font-semibold text-slate-900">{project.client_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 font-medium">Project Manager</p>
                      {editingPM ? (
                        <select
                          value={project.assigned_manager || ''}
                          onChange={(e) => {
                            updatePM(e.target.value);
                            setEditingPM(false);
                          }}
                          onBlur={() => setEditingPM(false)}
                          autoFocus
                          className="font-semibold text-slate-900 border border-blue-500 rounded px-2 py-1"
                        >
                          <option value="">No PM</option>
                          {managers.map(manager => (
                            <option key={manager.id} value={manager.full_name}>
                              {manager.full_name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p 
                          className="font-semibold text-slate-900 cursor-pointer hover:text-blue-600"
                          onClick={() => setEditingPM(true)}
                          title="Click to change PM"
                        >
                          {project.assigned_manager || 'No PM'} ✏️
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 font-medium">Quote Reference</p>
                      <p className="font-semibold text-slate-900">{project.quote_number || project.quote_id || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 font-medium">Description</p>
                      <p className="font-semibold text-slate-900">{project.description || project.title}</p>
                    </div>
                  </div>
                </div>

                {/* Financial Summary */}
                <div className="bg-gradient-to-br from-white/80 to-blue-50/50 backdrop-blur-xl p-6 rounded-xl border border-blue-200/40 shadow-lg">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-900">
                    Financial Overview
                    {project.quickbooks_imported && (
                      <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded border border-green-300">QuickBooks</span>
                    )}
                  </h3>
                  
                  <div className="space-y-3">
                    {/* Revenue — Invoices */}
                    <div className="text-center p-4 bg-gradient-to-br from-blue-100/70 to-cyan-100/70 rounded-xl border border-blue-300">
                      <p className="text-xs text-slate-600 font-semibold">INVOICED</p>
                      <p className="text-2xl font-bold text-blue-700">{formatCurrency(totalInvoices)}</p>
                      <p className="text-[10px] text-slate-500">{invoices.length} invoice(s)</p>
                    </div>

                    {/* Cost Breakdown */}
                    <div className="space-y-2">
                      <p className="text-xs text-slate-600 font-bold uppercase">Costs</p>
                      
                      {/* PO Total */}
                      <div className="flex justify-between items-center py-1.5 border-b border-slate-200">
                        <button onClick={() => setActiveTab('purchase-orders')} className="text-sm text-blue-700 hover:text-blue-900 hover:underline font-medium">Purchase Orders</button>
                        <span className="text-sm font-bold text-slate-900">{formatCurrency(purchaseOrders.reduce((s, po) => s + (po.total_amount || po.amount || 0), 0))}</span>
                      </div>
                      <p className="text-[10px] text-slate-500 -mt-1">{purchaseOrders.length} PO(s)</p>

                      {/* Labour */}
                      <div className="flex justify-between items-center py-1.5 border-b border-slate-200">
                        <button onClick={() => setActiveTab('team')} className="text-sm text-blue-700 hover:text-blue-900 hover:underline font-medium">Labour</button>
                        <span className="text-sm font-bold text-slate-900">{formatCurrency(shopTrackerData?.total_labour_cost || 0)}</span>
                      </div>
                      <p className="text-[10px] text-slate-500 -mt-1" data-testid="labour-cost-note">
                        {shopTrackerData?.total_hours?.toFixed(1) || 0}h logged
                        {shopTrackerData?.rates_set
                          ? ` · actual: ${formatCurrency(shopTrackerData.labour_wage_cost || 0)} wages + ${formatCurrency(shopTrackerData.labour_burden_cost || 0)} burden ($${shopTrackerData.burden_per_hour}/hr)`
                          : ' · estimate — no pay rates loaded yet (Admin → Pay Rates)'}
                      </p>
                      {shopTrackerData?.rates_set && shopTrackerData?.rate_missing?.length > 0 && (
                        <p className="text-[10px] text-amber-700 -mt-0.5" data-testid="labour-rate-missing" title={shopTrackerData.rate_missing.join(', ')}>
                          {shopTrackerData.rate_missing.length} {shopTrackerData.rate_missing.length === 1 ? 'person has' : 'people have'} no pay rate set — costed at the default wage
                        </p>
                      )}
                      <div className="flex justify-between items-center py-1.5 border-b border-slate-200">
                        <button onClick={() => setActiveTab('materials')} className="text-sm text-blue-700 hover:text-blue-900 hover:underline font-medium">Consumables</button>
                        <span className="text-sm font-bold text-slate-900">{formatCurrency(shopTrackerData?.total_consumable_cost || 0)}</span>
                      </div>
                      <p className="text-[10px] text-slate-500 -mt-1">{shopTrackerData?.consumable_entries?.length || 0} item(s)</p>
                    </div>

                    {/* Total Costs */}
                    {(() => {
                      const poTotal = purchaseOrders.reduce((s, po) => s + (po.total_amount || po.amount || 0), 0);
                      const labourTotal = shopTrackerData?.total_labour_cost || 0;
                      const consTotal = shopTrackerData?.total_consumable_cost || 0;
                      const totalCost = poTotal + labourTotal + consTotal;
                      const profit = totalInvoices - totalCost;
                      const margin = totalInvoices > 0 ? (profit / totalInvoices * 100) : 0;
                      return (
                        <>
                          <div className="flex justify-between items-center py-2 border-t-2 border-slate-300">
                            <span className="text-sm font-bold text-slate-800">Total Costs</span>
                            <span className="text-sm font-bold text-red-600">{formatCurrency(totalCost)}</span>
                          </div>

                          {/* Profit / Loss */}
                          <div className={`text-center p-4 rounded-xl border ${profit >= 0 ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`}>
                            <p className="text-xs font-semibold text-slate-600">{profit >= 0 ? 'PROFIT' : 'LOSS'}</p>
                            <p className={`text-2xl font-bold ${profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                              {formatCurrency(Math.abs(profit))}
                            </p>
                            <p className="text-[10px] text-slate-500">{margin.toFixed(1)}% margin</p>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                    {project.quickbooks_imported && (
                      <>
                        {/* QuickBooks Data */}
                        <div className="space-y-3 border-t border-blue-200/50 pt-4">
                          <div className="flex justify-between">
                            <span className="text-slate-600 font-medium">QB Revenue:</span>
                            <span className="font-bold text-green-600">{formatCurrency(project.actual_revenue || 0)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600 font-medium">QB Cost:</span>
                            <span className="font-bold text-red-600">{formatCurrency(project.actual_cost || 0)}</span>
                          </div>
                          <div className="flex justify-between border-t border-blue-200/50 pt-2">
                            <span className="text-slate-600 font-medium">QB Profit:</span>
                            <span className={`font-bold ${
                              (project.profit_loss || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {formatCurrency(project.profit_loss || 0)}
                            </span>
                          </div>
                          {project.profit_percentage !== undefined && (
                            <div className="flex justify-between">
                              <span className="text-slate-600 font-medium">Profit Margin:</span>
                              <span className={`font-bold ${
                                project.profit_percentage >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {project.profit_percentage.toFixed(1)}%
                              </span>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>

              {/* Site Information for SMS Dispatch System */}
              {(project.site_name || project.site_address || project.site_contact_name || project.site_contact_phone || project.site_contact_email) && (
                <div className="bg-gradient-to-br from-white/80 to-blue-50/50 backdrop-blur-xl p-6 rounded-xl border border-blue-200/40 shadow-lg">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-900">
                    <span>📱</span> Site Information (SMS Dispatch)
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      {project.site_name && (
                        <div className="flex items-start space-x-3">
                          <MapPin className="h-5 w-5 text-blue-600 mt-0.5" />
                          <div>
                            <p className="text-sm text-slate-600 font-medium">Site Name</p>
                            <p className="font-semibold text-slate-900">{project.site_name}</p>
                          </div>
                        </div>
                      )}
                      
                      {project.site_address && (
                        <div className="flex items-start space-x-3">
                          <MapPin className="h-5 w-5 text-green-600 mt-0.5" />
                          <div>
                            <p className="text-sm text-slate-600 font-medium">Site Address</p>
                            <p className="font-semibold text-slate-900">{project.site_address}</p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-4">
                      {project.site_contact_name && (
                        <div className="flex items-start space-x-3">
                          <Users className="h-5 w-5 text-purple-700 mt-0.5" />
                          <div>
                            <p className="text-sm text-slate-500">Site Contact</p>
                            <p className="font-medium">{project.site_contact_name}</p>
                          </div>
                        </div>
                      )}
                      
                      {project.site_contact_phone && (
                        <div className="flex items-start space-x-3">
                          <Phone className="h-5 w-5 text-amber-700 mt-0.5" />
                          <div>
                            <p className="text-sm text-slate-500">Contact Phone</p>
                            <p className="font-medium">
                              <a href={`tel:${project.site_contact_phone}`} className="hover:text-blue-700 transition-colors">
                                {project.site_contact_phone}
                              </a>
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {project.site_contact_email && (
                        <div className="flex items-start space-x-3">
                          <Mail className="h-5 w-5 text-cyan-700 mt-0.5" />
                          <div>
                            <p className="text-sm text-slate-500">Contact Email</p>
                            <p className="font-medium">
                              <a href={`mailto:${project.site_contact_email}`} className="hover:text-blue-700 transition-colors">
                                {project.site_contact_email}
                              </a>
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'billing' && (
            <div className="bg-white/60 backdrop-blur-xl rounded-lg border border-slate-200">
              <div className="p-6 border-b border-slate-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-slate-900">Invoices & Billing</h3>
                  <div className="text-sm text-slate-600">
                    {invoices.length} invoice(s) • Total: ${project?.total_value?.toFixed(2) || '0.00'}
                  </div>
                </div>
              </div>
              <div className="p-6">
                {invoices && invoices.length > 0 ? (
                  <div className="space-y-4">
                    {invoices.map((invoice) => (
                      <div
                        key={invoice.id}
                        className="p-6 bg-white/70 backdrop-blur-sm rounded-lg border border-slate-200 hover:border-blue-500 transition-colors shadow-md"
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h4 className="font-semibold text-slate-900">Invoice Number: {invoice.our_invoice_number || invoice.invoice_id}</h4>
                            <p className="text-sm text-slate-700 mt-1">{invoice.description}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm border ${
                            invoice.invoice_status === 'PAID' ? 'bg-green-100 text-green-800 border-green-300' :
                            invoice.invoice_status === 'SENT' || invoice.invoice_status === 'Sent' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                            'bg-yellow-100 text-yellow-800 border-yellow-300'
                          }`}>
                            {invoice.invoice_status || 'WIP'}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-slate-600 font-medium">Amount</p>
                            <p className="text-slate-900 font-semibold">{formatCurrency(invoice.total_amount || invoice.invoice_amount || 0)}</p>
                          </div>
                          <div>
                            <p className="text-slate-600 font-medium">Date</p>
                            <p className="text-slate-900">{invoice.invoice_sent_date ? formatDate(invoice.invoice_sent_date) : 'Not sent'}</p>
                          </div>
                          <div>
                            <p className="text-slate-600 font-medium">Terms</p>
                            <p className="text-slate-900">{invoice.terms || 'Net 30'}</p>
                          </div>
                          <div>
                            <p className="text-slate-600 font-medium">PO Number</p>
                            <p className="text-slate-900">{invoice.po_number || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Summary totals */}
                    <div className="bg-slate-100 rounded-lg p-4 border-2 border-blue-600">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <div className="text-sm text-slate-500">Total Subtotal</div>
                          <div className="font-bold text-lg">${project.budget_breakdown?.subtotal?.toFixed(2) || '0.00'}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-500">Total Tax</div>
                          <div className="font-bold text-lg">${project.budget_breakdown?.tax?.toFixed(2) || '0.00'}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-500">Grand Total</div>
                          <div className="font-bold text-xl text-green-700">${project.budget_breakdown?.total?.toFixed(2) || '0.00'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No invoices found for this project</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'purchase-orders' && (
            <div className="bg-white/60 backdrop-blur-xl rounded-lg border border-slate-200">
              <div className="p-6 border-b border-slate-200">
                <div className="flex justify-between items-center flex-wrap gap-3">
                  <h3 className="text-lg font-semibold text-slate-900">Purchase Orders</h3>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="text-sm text-slate-700">
                      {purchaseOrders.length} PO(s) • Total: {formatCurrency(purchaseOrders.reduce((sum, po) => sum + (po.total_amount || 0), 0))}
                    </div>
                    <select id="po-sort" defaultValue="date-desc" onChange={e => {
                      const sel = e.target.value;
                      setPurchaseOrders(prev => [...prev].sort((a, b) => {
                        if (sel === 'date-desc') return poDateKey(b.date).localeCompare(poDateKey(a.date));
                        if (sel === 'date-asc') return poDateKey(a.date).localeCompare(poDateKey(b.date));
                        if (sel === 'amount-desc') return (b.total_amount || 0) - (a.total_amount || 0);
                        if (sel === 'amount-asc') return (a.total_amount || 0) - (b.total_amount || 0);
                        if (sel === 'ordered-by') return (a.ordered_by || '').localeCompare(b.ordered_by || '');
                        if (sel === 'po-number') return (a.po_number || '').localeCompare(b.po_number || '', undefined, {numeric: true});
                        return 0;
                      }));
                    }} className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-sm text-slate-900 outline-none">
                      <option value="date-desc">Newest First</option>
                      <option value="date-asc">Oldest First</option>
                      <option value="amount-desc">Highest Amount</option>
                      <option value="amount-asc">Lowest Amount</option>
                      <option value="ordered-by">Ordered By</option>
                      <option value="po-number">PO Number</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="p-6">
                {purchaseOrders && purchaseOrders.length > 0 ? (
                  <div className="space-y-4">
                    {purchaseOrders.map((po, index) => (
                      <div key={po.id || index} className="bg-white/70 backdrop-blur-sm rounded-lg p-4 border border-slate-200">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <div className="text-sm text-slate-600">PO Number</div>
                            <div className="font-medium text-blue-600">{po.po_number || `PO-${String(index + 1).padStart(3, '0')}`}</div>
                          </div>
                          <div>
                            <div className="text-sm text-slate-600">Date</div>
                            <div className="font-medium flex items-center space-x-1 text-slate-900">
                              <CalendarIcon className="h-4 w-4" />
                              <span>{po.date || 'N/A'}</span>
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-slate-600">Status</div>
                            <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                              po.status === 'received' 
                                ? 'bg-green-100 text-green-800'
                                : po.status === 'partial'
                                ? 'bg-yellow-100 text-yellow-800'
                                : po.status === 'cancelled'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {po.status || 'active'}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-slate-600">Total Amount</div>
                            <div className="font-bold text-lg text-green-600">{formatCurrency(po.total_amount || 0)}</div>
                          </div>
                          <div className="md:col-span-2">
                            <div className="text-sm text-slate-600">Company</div>
                            <div className="font-medium flex items-center space-x-1 text-slate-900">
                              <Building2 className="h-4 w-4" />
                              <span>{po.company_name || 'N/A'}</span>
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-slate-600">Ordered By</div>
                            <div className="font-medium flex items-center space-x-1 text-slate-900">
                              <User className="h-4 w-4" />
                              <span>{po.ordered_by || 'N/A'}</span>
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-slate-600">Work Order</div>
                            <div className="font-medium text-slate-900">{po.work_order_number || projectNumber}</div>
                          </div>
                          <div className="md:col-span-4">
                            <div className="text-sm text-slate-600">Description</div>
                            <div className="font-medium text-sm text-slate-900">{po.description || 'No description'}</div>
                          </div>
                          {po.contact && (
                            <div className="md:col-span-2">
                              <div className="text-sm text-slate-600">Contact</div>
                              <div className="font-medium text-sm text-slate-900">{po.contact}</div>
                            </div>
                          )}
                          {po.notes && (
                            <div className="md:col-span-2">
                              <div className="text-sm text-slate-600">Notes</div>
                              <div className="font-medium text-sm text-slate-900">{po.notes}</div>
                            </div>
                          )}
                          <div>
                            <div className="text-sm text-slate-600">Amount</div>
                            <div className="font-medium text-slate-900">{formatCurrency(po.amount || 0)}</div>
                          </div>
                          <div>
                            <div className="text-sm text-slate-600">Tax</div>
                            <div className="font-medium text-slate-900">{formatCurrency(po.tax || 0)}</div>
                          </div>
                        </div>
                        
                        {/* Action buttons */}
                        <div className="mt-4 flex justify-end space-x-2">
                          <button className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors">
                            Edit
                          </button>
                          <button className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors">
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    {/* Summary totals */}
                    <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border-2 border-orange-500">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <div className="text-sm text-slate-700">Total Amount (excl. tax)</div>
                          <div className="font-bold text-lg text-slate-900">{formatCurrency(purchaseOrders.reduce((sum, po) => sum + (po.amount || 0), 0))}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-700">Total Tax</div>
                          <div className="font-bold text-lg text-slate-900">{formatCurrency(purchaseOrders.reduce((sum, po) => sum + (po.tax || 0), 0))}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-700">Grand Total</div>
                          <div className="font-bold text-xl text-orange-600">{formatCurrency(purchaseOrders.reduce((sum, po) => sum + (po.total_amount || 0), 0))}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-600">
                    <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No purchase orders found for this project</p>
                    {poLoading && (
                      <div className="mt-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'team' && (
            <ProjectLabourTab data={shopTrackerData} project={project} projectNumber={projectNumber} />
          )}

          {/* Daily Labor Breakdown Modal */}
          {showDailyBreakdown && selectedEmployee && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
              <div className="bg-white/60 backdrop-blur-xl rounded-lg border border-slate-200 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
                <div className="p-6 border-b border-slate-200">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">
                      Daily Breakdown - {selectedEmployee.employee_name}
                    </h3>
                    <button
                      onClick={() => setShowDailyBreakdown(false)}
                      className="text-slate-500 hover:text-slate-900"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="mt-2 text-sm text-slate-500">
                    Project: {projectNumber} • Total: {selectedEmployee.total_hours.toFixed(1)}h 
                    • Days Worked: {selectedEmployee.days_worked}
                  </div>
                </div>

                <div className="p-6">
                  {/* Employee Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Clock className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="text-sm text-slate-500">Total Hours</p>
                          <p className="font-bold">{selectedEmployee.total_hours.toFixed(1)}h</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Clock className="h-5 w-5 text-green-600" />
                        <div>
                          <p className="text-sm text-slate-500">Work Days</p>
                          <p className="font-bold">{selectedEmployee.days_worked}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Clock className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="text-sm text-slate-500">Average Daily</p>
                          <p className="font-bold">{(selectedEmployee.total_hours / selectedEmployee.days_worked).toFixed(1)}h</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <CalendarIcon className="h-5 w-5 text-purple-700" />
                        <div>
                          <p className="text-sm text-slate-500">Days</p>
                          <p className="font-bold">{selectedEmployee.days_worked}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Daily Breakdown */}
                  <div>
                    <h4 className="text-md font-semibold mb-4 text-slate-700">Daily Schedule</h4>
                    <div className="space-y-2">
                      {selectedEmployee.daily_breakdown.map((day, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center space-x-4">
                            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                            <div>
                              <p className="font-medium">{new Date(day.work_date).toLocaleDateString('en-US', { 
                                weekday: 'long', 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              })}</p>
                              {day.work_type && (
                                <p className="text-sm text-slate-500">{day.work_type}</p>
                              )}
                              {day.section && (
                                <p className="text-xs text-slate-500">{day.section}</p>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-6 text-sm">
                            <div className="text-blue-600">
                              <span className="font-medium">{day.hours_worked.toFixed(1)}h</span>
                              <span className="text-slate-500 ml-1">({day.duration_display})</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'equipment' && (
            <div className="space-y-6">
              <div className="bg-white/60 backdrop-blur-xl rounded-lg border border-slate-200">
                <div className="p-6 border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800">Company Equipment On Site</h3>
                </div>
                <div className="p-6">
                  <p className="text-slate-500 text-center py-8">Equipment tracking functionality coming soon.</p>
                </div>
              </div>

              <div className="bg-white/60 backdrop-blur-xl rounded-lg border border-slate-200">
                <div className="p-6 border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800">Rental Equipment</h3>
                </div>
                <div className="p-6">
                  <p className="text-slate-500 text-center py-8">Rental equipment tracking functionality coming soon.</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'materials' && (
            <div className="space-y-6">
              {shopTrackerData && shopTrackerData.consumable_entries?.length > 0 ? (
                <div className="bg-white/60 backdrop-blur-xl rounded-lg border border-slate-200 overflow-hidden">
                  <div className="p-6 border-b border-slate-200">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                        <Package className="h-5 w-5 text-emerald-600" /> Consumables Used
                      </h3>
                      <span className="text-sm font-semibold text-emerald-600">
                        ${shopTrackerData.total_consumable_cost.toFixed(2)} total
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-slate-500 border-b border-slate-200">
                          <th className="pb-3 text-left font-semibold">Item</th>
                          <th className="pb-3 text-left font-semibold">Logged By</th>
                          <th className="pb-3 text-left font-semibold">Date</th>
                          <th className="pb-3 text-center font-semibold">Qty</th>
                          <th className="pb-3 text-right font-semibold">Cost</th>
                          <th className="pb-3 text-right font-semibold w-28">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shopTrackerData.consumable_entries.map((c, i) => (
                          editingConsId === c.id ? (
                            <tr key={c.id || i} className="border-b border-slate-100 bg-blue-50/50" data-testid={`cons-edit-row-${c.id}`}>
                              <td className="py-2 pr-2">
                                <input
                                  value={consEdit.item_name}
                                  onChange={(e) => setConsEdit({ ...consEdit, item_name: e.target.value })}
                                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                  data-testid="cons-edit-name"
                                />
                              </td>
                              <td className="py-2 text-slate-500 text-xs">{c.employee_name}</td>
                              <td className="py-2 text-slate-500 text-xs">{c.date}</td>
                              <td className="py-2 px-1">
                                <input
                                  type="number" step="any" min="0"
                                  value={consEdit.quantity}
                                  onChange={(e) => setConsEdit({ ...consEdit, quantity: e.target.value })}
                                  className="w-16 border border-slate-300 rounded px-2 py-1.5 text-sm text-center focus:ring-2 focus:ring-blue-500 outline-none"
                                  data-testid="cons-edit-qty"
                                />
                              </td>
                              <td className="py-2 pl-1">
                                <div className="flex items-center justify-end gap-1">
                                  <span className="text-slate-500 text-xs">$</span>
                                  <input
                                    type="number" step="0.01" min="0"
                                    value={consEdit.unit_cost}
                                    onChange={(e) => setConsEdit({ ...consEdit, unit_cost: e.target.value })}
                                    className="w-20 border border-slate-300 rounded px-2 py-1.5 text-sm text-right focus:ring-2 focus:ring-blue-500 outline-none"
                                    title="Unit cost — line total recalculates automatically"
                                    data-testid="cons-edit-unitcost"
                                  />
                                  <span className="text-slate-500 text-[10px]">/ea</span>
                                </div>
                                <p className="text-[10px] text-slate-500 text-right mt-0.5">= ${((Number(consEdit.quantity) || 0) * (Number(consEdit.unit_cost) || 0)).toFixed(2)}</p>
                              </td>
                              <td className="py-2 text-right">
                                <div className="flex items-center justify-end gap-1.5">
                                  <button onClick={() => saveConsEdit(c.id)} disabled={consSaving}
                                    className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-semibold disabled:opacity-60"
                                    data-testid="cons-edit-save">
                                    {consSaving ? '…' : 'Save'}
                                  </button>
                                  <button onClick={cancelConsEdit} disabled={consSaving}
                                    className="px-2 py-1 text-slate-500 hover:text-slate-700 text-xs"
                                    data-testid="cons-edit-cancel">
                                    Cancel
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            <tr key={c.id || i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cons-row-${c.id}`}>
                              <td className="py-3 text-slate-800 font-medium">{c.item_name}
                                {c.source === 'field' && (
                                  <span className="ml-2 text-[10px] font-semibold uppercase rounded px-1.5 py-0.5 bg-sky-50 text-sky-700 border border-sky-200" title="Added on the Field Tracker timesheet" data-testid={`cons-field-badge-${c.id}`}>
                                    {c.kind === 'purchase' ? 'Purchase · field sheet' : c.kind === 'equipment' ? 'Equipment · field sheet' : 'Field sheet'}
                                  </span>
                                )}
                              </td>
                              <td className="py-3 text-slate-600">{c.employee_name}</td>
                              <td className="py-3 text-slate-500">{c.date}</td>
                              <td className="py-3 text-center text-slate-800 font-semibold">{c.quantity}</td>
                              <td className="py-3 text-right text-emerald-600 font-medium">${(c.total_cost || 0).toFixed(2)}</td>
                              <td className="py-3 text-right">
                                <button onClick={() => startConsEdit(c)}
                                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-medium hover:underline"
                                  data-testid={`cons-edit-btn-${c.id}`}>
                                  <Edit className="h-3.5 w-3.5" /> Edit
                                </button>
                              </td>
                            </tr>
                          )
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-slate-200">
                          <td colSpan={3} className="py-3 text-slate-600 font-semibold">Total</td>
                          <td className="py-3 text-center text-slate-600 font-semibold">
                            {shopTrackerData.consumable_entries.reduce((s, c) => s + c.quantity, 0)}
                          </td>
                          <td className="py-3 text-right text-emerald-600 font-bold">${shopTrackerData.total_consumable_cost.toFixed(2)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <div className="px-6 py-3 bg-slate-50 border-t border-slate-200">
                    <p className="text-xs text-slate-500">Live data from the Shop Tracker and Field Tracker timesheets</p>
                  </div>
                </div>
              ) : (
                <div className="bg-white/60 backdrop-blur-xl rounded-lg border border-slate-200">
                  <div className="p-6 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-800">Materials & Consumables</h3>
                  </div>
                  <div className="p-6">
                    <div className="text-center py-8">
                      <Package className="h-12 w-12 mx-auto mb-4 text-slate-500 opacity-50" />
                      <p className="text-slate-500">No consumables logged for this project yet.</p>
                      <p className="text-sm text-slate-500 mt-1">Consumables logged in the Shop Tracker will appear here automatically.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* T&M Invoice Generator Modal */}
      {showInvoiceGen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">Generate T&M Invoice</h3>
              <button onClick={() => setShowInvoiceGen(false)} className="text-slate-500 hover:text-slate-600 text-xl">&times;</button>
            </div>

            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs font-bold text-slate-600 uppercase mb-2">Project</p>
                <p className="text-sm text-slate-900 font-semibold">{project?.project_number} — {project?.client_name}</p>
                <p className="text-xs text-slate-500">{project?.title || project?.description}</p>
              </div>

              <div className="space-y-3">
                <p className="text-xs font-bold text-slate-600 uppercase">Markup Percentages</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-slate-600">Labour %</label>
                    <input type="number" value={invoiceMarkup.labour} onChange={e => setInvoiceMarkup({...invoiceMarkup, labour: parseFloat(e.target.value) || 0})}
                      onFocus={e => e.target.select()} data-testid="invoice-markup-labour"
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" placeholder="15" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600">Consumables %</label>
                    <input type="number" value={invoiceMarkup.consumables} onChange={e => setInvoiceMarkup({...invoiceMarkup, consumables: parseFloat(e.target.value) || 0})}
                      onFocus={e => e.target.select()} data-testid="invoice-markup-consumables"
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" placeholder="15" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600">Materials %</label>
                    <input type="number" value={invoiceMarkup.materials} onChange={e => setInvoiceMarkup({...invoiceMarkup, materials: parseFloat(e.target.value) || 0})}
                      onFocus={e => e.target.select()} data-testid="invoice-markup-materials"
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" placeholder="15" />
                  </div>
                </div>
              </div>

              {/* Add Consumables to Job */}
              <div className="space-y-2">
                <p className="text-xs font-bold text-slate-600 uppercase">Add Consumables to Job</p>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input type="text" value={invoiceConsSearch} onChange={async e => {
                      setInvoiceConsSearch(e.target.value);
                      if (e.target.value.length > 1 && invoiceConsCatalog.length === 0) {
                        const r = await axios.get(`${backendUrl}/api/shop/catalog`, { headers });
                        setInvoiceConsCatalog(r.data || []);
                      }
                    }} placeholder="Search consumable..."
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" />
                    {invoiceConsSearch.length > 1 && (
                      <div className="absolute z-20 bottom-full mb-1 w-full bg-white border border-slate-300 rounded-lg max-h-40 overflow-y-auto shadow-lg">
                        {invoiceConsCatalog.filter(c => c.name.toLowerCase().includes(invoiceConsSearch.toLowerCase())).slice(0, 8).map(c => (
                          <button key={c.name} onClick={() => {
                            setInvoiceConsItems(prev => ({...prev, [c.name]: (prev[c.name] || 0) + 1}));
                            setInvoiceConsSearch('');
                          }} className="w-full text-left px-3 py-1.5 hover:bg-blue-50 text-xs text-slate-900 border-b border-slate-100">
                            {c.name} <span className="text-slate-500">{c.category}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {Object.keys(invoiceConsItems).length > 0 && (
                  <div className="space-y-1">
                    {Object.entries(invoiceConsItems).map(([name, qty]) => (
                      <div key={name} className="flex items-center justify-between px-2 py-1 bg-amber-50 rounded border border-amber-200 text-xs">
                        <span className="text-slate-900">{name}</span>
                        <div className="flex items-center gap-1">
                          <button onClick={() => setInvoiceConsItems(prev => {
                            const c = {...prev}; c[name] = Math.max(0, (c[name]||0) - 1); if (c[name] <= 0) delete c[name]; return c;
                          })} className="w-5 h-5 bg-red-500 text-white rounded text-xs font-bold flex items-center justify-center">-</button>
                          <span className="font-bold w-4 text-center">{qty}</span>
                          <button onClick={() => setInvoiceConsItems(prev => ({...prev, [name]: (prev[name]||0) + 1}))}
                            className="w-5 h-5 bg-emerald-600 text-white rounded text-xs font-bold flex items-center justify-center">+</button>
                        </div>
                      </div>
                    ))}
                    <button onClick={async () => {
                      let added = 0;
                      for (const [name, qty] of Object.entries(invoiceConsItems)) {
                        try {
                          await axios.post(`${backendUrl}/api/shop/consumable-entries`, {
                            work_order_number: project?.project_number,
                            work_order_customer: project?.client_name || '',
                            date: new Date().toISOString().slice(0, 10),
                            item_name: name, quantity: qty, unit: 'each', unit_cost: 0,
                            notes: 'Added from T&M invoice',
                          }, { headers });
                          added++;
                        } catch {}
                      }
                      toast.success(`Added ${added} consumable(s) to ${project?.project_number}`);
                      setInvoiceConsItems({});
                      fetchProjectData();
                    }} className="w-full py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-medium">
                      Add {Object.keys(invoiceConsItems).length} Consumable(s) to Job
                    </button>
                  </div>
                )}
              </div>

              {/* Preview */}
              {(() => {
                const labourCost = shopTrackerData?.total_labour_cost || 0;
                const consCost = shopTrackerData?.total_consumable_cost || 0;
                const poCost = purchaseOrders.reduce((s, po) => s + (po.total_amount || po.amount || 0), 0);
                const labourBill = labourCost * (1 + invoiceMarkup.labour / 100);
                const consBill = consCost * (1 + invoiceMarkup.consumables / 100);
                const matBill = poCost * (1 + invoiceMarkup.materials / 100);
                const totalBill = labourBill + consBill + matBill;

                return (
                  <div className="space-y-2">
                    <p className="text-xs font-bold text-slate-600 uppercase">Invoice Preview</p>
                    <div className="bg-slate-50 rounded-lg p-3 space-y-1.5 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Labour ({shopTrackerData?.total_hours?.toFixed(1) || 0}h)</span>
                        <div className="text-right">
                          <span className="text-slate-500 text-xs mr-2">{formatCurrency(labourCost)}</span>
                          <span className="font-bold text-slate-900">{formatCurrency(labourBill)}</span>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Consumables ({shopTrackerData?.consumable_entries?.length || 0} items)</span>
                        <div className="text-right">
                          <span className="text-slate-500 text-xs mr-2">{formatCurrency(consCost)}</span>
                          <span className="font-bold text-slate-900">{formatCurrency(consBill)}</span>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Materials/POs ({purchaseOrders.length})</span>
                        <div className="text-right">
                          <span className="text-slate-500 text-xs mr-2">{formatCurrency(poCost)}</span>
                          <span className="font-bold text-slate-900">{formatCurrency(matBill)}</span>
                        </div>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-slate-300">
                        <span className="font-bold text-slate-900">TOTAL</span>
                        <span className="font-bold text-emerald-700 text-lg">{formatCurrency(totalBill)}</span>
                      </div>
                    </div>

                    {totalInvoices > 0 && Math.abs(totalInvoices - totalBill) > 0.01 && (
                      <div className="flex justify-between items-center text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="tm-workorder-price-note">
                        <span className="text-amber-700">Work-order price on file</span>
                        <span className="font-bold text-amber-800">{formatCurrency(totalInvoices)}</span>
                      </div>
                    )}

                    <button onClick={handleGenerateInvoice}
                      data-testid="tm-print-invoice-btn"
                      className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-sm">
                      Print Invoice
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Work-order price reconciliation popup */}
      {reconcileData && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="tm-reconcile-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-slate-900">Price doesn't match</h3>
              <button onClick={() => setReconcileData(null)} className="text-slate-500 hover:text-slate-600 text-xl" data-testid="tm-reconcile-close">&times;</button>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              The price on this work order doesn't match what 365Ops calculated. How would you like to handle it?
            </p>

            <div className="grid grid-cols-2 gap-3 mb-5">
              <div className="text-center p-3 rounded-xl border border-slate-200 bg-slate-50">
                <p className="text-[11px] font-semibold text-slate-500 uppercase">Work-order price</p>
                <p className="text-xl font-bold text-slate-900" data-testid="tm-reconcile-wo-price">{formatCurrency(reconcileData.workOrderPrice)}</p>
              </div>
              <div className="text-center p-3 rounded-xl border border-emerald-200 bg-emerald-50">
                <p className="text-[11px] font-semibold text-emerald-700 uppercase">365Ops price</p>
                <p className="text-xl font-bold text-emerald-700" data-testid="tm-reconcile-ops-price">{formatCurrency(reconcileData.currentTotal)}</p>
              </div>
            </div>

            <div className="space-y-2.5">
              <button onClick={matchWorkOrderPrice} disabled={reconcileBusy}
                data-testid="tm-reconcile-match-btn"
                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-sm text-left disabled:opacity-60">
                Use the work-order price
                <span className="block text-xs font-normal text-blue-800">Scale the invoice up to {formatCurrency(reconcileData.workOrderPrice)} with markup</span>
              </button>
              <button onClick={overwriteWorkOrderPrice} disabled={reconcileBusy}
                data-testid="tm-reconcile-overwrite-btn"
                className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold text-sm text-left disabled:opacity-60">
                {reconcileBusy ? 'Updating…' : 'Overwrite work order with 365Ops price'}
                <span className="block text-xs font-normal text-emerald-800">Set the work order to {formatCurrency(reconcileData.currentTotal)} and print at that price</span>
              </button>
              <button onClick={() => setReconcileData(null)} disabled={reconcileBusy}
                data-testid="tm-reconcile-cancel-btn"
                className="w-full py-2 text-sm font-medium text-slate-500 hover:text-slate-700">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;